import * as React from "react"
import * as Antd from "antd"
import styles from "./LoginForm.module.scss"

import { AppRoutes } from "@constants/AppRoutes.ts"
import { AppMessage } from "@others/AppMessage.ts"
import { AppFunctions } from "@others/AppFunctions.ts"
import { useLoginForm } from "@hooks/useLoginForm.tsx"
import { useTranslation } from "react-i18next"
import { AppNotification } from "@others/AppNotification.ts"
import { AppLocalStorageKeys } from "@constants/AppLocalStorageKeys.ts"
import { AppAuthorizedProfiles } from "@constants/AppAuthorizedProfiles.ts"
import { useLocation, useNavigate } from "react-router-dom"
import { IExternalMembershipApplies } from "@pages/ExternalMembershipAppliesFlow/ExternalMembershipAppliesFlow.tsx"

import DvInput from "@components/dataEntry/DvInput/DvInput.tsx"
import DsButton from "@components/general/DsButton/DsButton.tsx"
import useAuthCtx from "@contexts/hooks/useAuthCtx.tsx"
import DvCheckbox from "@components/dataEntry/DvCheckbox/DvCheckbox.tsx"
import DvAntdForm from "@components/others/DvAntdForm/DvAntdForm.tsx"
import authService from "@services/authService/authService.ts"
import InputCaption from "@components/feedback/InputCaption/InputCaption.tsx"
import DvAntdFormItem from "@components/others/DvAntdFormItem/DvAntdFormItem.tsx"

export interface ILoginFormFieldType {
	email?: string
	password?: string
	remember?: boolean
}

interface IProps {
	className?: string
}

const LoginForm: React.FC<IProps> = (props) => {
	const { t } = useTranslation()
	const { setIsLogged, setLoggedUser } = useAuthCtx()

	const navigate = useNavigate()
	const location = useLocation()
	const lastUrlParam = AppFunctions.getUrlParam("lastUrl")

	const [form] = Antd.Form.useForm<ILoginFormFieldType>()
	const [loginLoading, setLoginLoading] = React.useState(false)

	const initialValues: ILoginFormFieldType = React.useMemo(
		() => ({
			remember: AppFunctions.isLoginRemembered() ?? true,
			email:
				location.state?.fromResetPasswordEmailValue ||
				location.state?.email,
		}),
		[location.state]
	)

	const { onChange, clearForm } = useLoginForm(form, initialValues)

	// membership external apply flow
	const externalMembershipApplies = location.state
		?.externalMembershipApplies as IExternalMembershipApplies | undefined

	const onFinish = ({ email, password }: ILoginFormFieldType) => {
		if (!email || !password) return

		setLoginLoading(true)

		authService
			.login({
				email,
				password,
			})
			.then(({ user }) => {
				setIsLogged(true)
				setLoggedUser(user)
				AppMessage.success(t("login_successful"))

				if (!AppFunctions.isLoginRemembered()) clearForm()

				if (user?.profileType === AppAuthorizedProfiles.BACKOFFICE) {
					navigate(AppRoutes.backofficeChat, { replace: true })
					return
				}

				if (location.state?.from) {
					navigate(location.state.from, { replace: true })
					return
				} else if (lastUrlParam) {
					const lastUrl = new URL(lastUrlParam).pathname
					navigate(lastUrl, { replace: true })
					return
				} else if (externalMembershipApplies) {
					navigate(
						AppRoutes.externalMembershipApplies +
							`?plan=${externalMembershipApplies?.plan}&paymentFrequency=${externalMembershipApplies?.paymentFrequency}`,
						{ replace: true }
					)
				} else {
					navigate(AppRoutes.userProfile, { replace: true })
				}
			})
			.catch(({ message }) => {
				AppNotification.error({
					message: message || t("something_went_wrong"),
				})
			})
			.finally(() => {
				setLoginLoading(false)
			})
	}

	React.useEffect(() => {
		if (
			AppFunctions.isAuthenticated() &&
			AppFunctions.isLoginRemembered()
		) {
			navigate(AppRoutes.home, { replace: true })
		}
	}, [])

	return (
		<DvAntdForm<ILoginFormFieldType>
			form={form}
			className={`${styles.login_form} ${props.className || ""}`}
			initialValues={initialValues}
			onFinish={onFinish}
		>
			<div className={styles.top}>
				<header className={styles.header}>
					{t("insert_email_and_password")}
				</header>

				<main className={styles.body}>
					<DvAntdFormItem<ILoginFormFieldType>
						label={t("email")}
						name="email"
						rules={[
							{
								required: true,
								message: t("please_enter_your_email"),
							},
							{
								type: "email",
								message: t("your_email_is_not_valid"),
							},
						]}
					>
						<DvInput
							placeholder={t("insert_your_email")}
							onChange={(e) => onChange("email", e.target.value)}
						/>
					</DvAntdFormItem>

					<div className={styles.password_container}>
						<DvAntdFormItem<ILoginFormFieldType>
							label={t("password")}
							name="password"
							rules={[
								{
									required: true,
									message: t("please_enter_your_password"),
								},
							]}
						>
							<DvInput.Password
								placeholder={t("insert_your_password")}
								onChange={(e) =>
									onChange("password", e.target.value)
								}
							/>
						</DvAntdFormItem>

						<InputCaption
							className={styles.input_caption}
							text={t("forgot_my_password")}
							onClick={() =>
								navigate(AppRoutes.requestResetPassword, {
									state: {
										fromLoginEmailInputValue:
											form.getFieldValue("email"),
									},
								})
							}
						/>
					</div>

					<DvAntdFormItem<ILoginFormFieldType>
						className={styles.remember_me_item}
						name="remember"
						valuePropName="checked"
					>
						<DvCheckbox
							defaultChecked={AppFunctions.isLoginRemembered()}
							onChange={({ target }) => {
								localStorage.setItem(
									AppLocalStorageKeys.REMEMBER_ME,
									target.checked.toString()
								)
							}}
						>
							{t("remember_me")}
						</DvCheckbox>
					</DvAntdFormItem>
				</main>
			</div>

			<div className={styles.footer}>
				<DsButton
					className={styles.login_button}
					htmlType="submit"
					loading={loginLoading}
				>
					{t("start_session")}
				</DsButton>

				<div className={styles.footer_text}>
					<label className={styles.label}>
						{t("dont_have_an_account_yet")}
					</label>

					<DsButton
						className={styles.sign_up_link}
						type="text"
						onClick={() => navigate(AppRoutes.validateAccount)}
					>
						{t("create_account")}
					</DsButton>
				</div>
			</div>
		</DvAntdForm>
	)
}

export default React.memo(LoginForm)
